import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useApp } from '../../context/AppContext';
import { Point } from '../../types';

interface DrawingCanvasProps {
  viewportWidth: number;
  viewportHeight: number;
  selectedStringOption: string;
  setSelectedStringOption: (val: string) => void;
  isDrawingActive: boolean;
  setIsDrawingActive: (active: boolean) => void;
  finishStringRef: React.MutableRefObject<(() => void) | null>;
}

export const DrawingCanvas: React.FC<DrawingCanvasProps> = ({
  viewportWidth,
  viewportHeight,
  selectedStringOption,
  setSelectedStringOption,
  isDrawingActive,
  setIsDrawingActive,
  finishStringRef,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  const {
    appMode,
    setAppMode,
    pdfScale,
    pixelsPerFoot,
    setPixelsPerFoot,
    pagePathsMap,
    currentPageIndex,
    addDrawnString,
    setStatus,
  } = useApp();

  const [calibratePoints, setCalibratePoints] = useState<Point[]>([]);
  const [currentActivePath, setCurrentActivePath] = useState<Point[]>([]);

  // Redraw existing paths on current page
  const redrawPaths = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const items = pagePathsMap[currentPageIndex] || [];
    ctx.strokeStyle = '#ef4444';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    items.forEach((item) => {
      if (item.hidden) return;
      const path = item.path || [];
      if (path.length > 0) {
        ctx.beginPath();
        let isFirst = true;
        for (let i = 0; i < path.length; i++) {
          const pt = path[i];
          if (!pt) {
            isFirst = true;
            continue;
          }
          if (isFirst) {
            ctx.moveTo(pt.x * pdfScale, pt.y * pdfScale);
            isFirst = false;
          } else {
            ctx.lineTo(pt.x * pdfScale, pt.y * pdfScale);
          }
        }
        ctx.stroke();
      }
    });

    // If currently calibrating and have 1 point
    if (calibratePoints.length === 1) {
      ctx.fillStyle = '#ef4444';
      ctx.beginPath();
      ctx.arc(
        calibratePoints[0].x * pdfScale,
        calibratePoints[0].y * pdfScale,
        5,
        0,
        2 * Math.PI
      );
      ctx.fill();
    }
  }, [pagePathsMap, currentPageIndex, pdfScale, calibratePoints]);

  useEffect(() => {
    redrawPaths();
  }, [redrawPaths, viewportWidth, viewportHeight]);

  // Finish current string callback
  const handleFinishString = useCallback(() => {
    if (currentActivePath.length > 0) {
      const pageItems = pagePathsMap[currentPageIndex] || [];
      let stringName = selectedStringOption;

      if (!stringName) {
        const defaultName = `String - ${pageItems.length + 1}`;
        stringName = prompt('Name this string path:', defaultName) || defaultName;
      }

      addDrawnString(stringName, currentActivePath);
      setCurrentActivePath([]);
      setIsDrawingActive(false);

      if (tooltipRef.current) {
        tooltipRef.current.style.display = 'none';
      }
    }
  }, [
    currentActivePath,
    pagePathsMap,
    currentPageIndex,
    selectedStringOption,
    addDrawnString,
    setIsDrawingActive,
  ]);

  // Bind ref for external finish button in toolbar
  useEffect(() => {
    finishStringRef.current = handleFinishString;
  }, [handleFinishString, finishStringRef]);

  // Handle pointer down (Click or touch to add points or calibrate)
  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (appMode === 'SELECT') return;
    if (!e.isPrimary) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.setPointerCapture(e.pointerId);

    const rect = canvas.getBoundingClientRect();
    const rawX = e.clientX - rect.left;
    const rawY = e.clientY - rect.top;

    const unzoomedX = rawX / pdfScale;
    const unzoomedY = rawY / pdfScale;

    // 1. Scale Calibration Mode
    if (appMode === 'CALIBRATE') {
      if (calibratePoints.length === 0) {
        setCalibratePoints([{ x: unzoomedX, y: unzoomedY }]);
        setStatus('Click the second point of the known dimension.', 'normal');
      } else if (calibratePoints.length === 1) {
        const pt1 = calibratePoints[0];
        const pt2 = { x: unzoomedX, y: unzoomedY };
        const dx = pt2.x - pt1.x;
        const dy = pt2.y - pt1.y;
        const pixelDist = Math.sqrt(dx * dx + dy * dy);

        const realDistRaw = prompt('What is the real-world distance between these two points in feet?');
        if (realDistRaw) {
          const realDist = parseFloat(realDistRaw.replace(/[^0-9.]/g, ''));
          if (!isNaN(realDist) && realDist > 0) {
            const ppf = pixelDist / realDist;
            setPixelsPerFoot(ppf);
            setStatus(`Scale calibrated: ${ppf.toFixed(2)} px/ft. You can now draw wire strings.`, 'success');
            setAppMode('DRAW_WIRE');
          } else {
            alert('Invalid distance. Please calibrate again.');
            setStatus('Invalid distance entered. Calibration reset.', 'error');
          }
        }
        setCalibratePoints([]);
      }
      return;
    }

    // 2. Draw Wire Mode
    if (appMode === 'DRAW_WIRE') {
      const pt = { x: unzoomedX, y: unzoomedY };
      if (!isDrawingActive) {
        setIsDrawingActive(true);
        setCurrentActivePath([pt]);
      } else {
        setCurrentActivePath((prev) => [...prev, pt]);
      }
    }
  };

  // Handle pointer move (Preview line segment and live measurement tooltip)
  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (appMode !== 'DRAW_WIRE' || !isDrawingActive || currentActivePath.length === 0) {
      if (tooltipRef.current) {
        tooltipRef.current.style.display = 'none';
      }
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const rawX = e.clientX - rect.left;
    const rawY = e.clientY - rect.top;

    redrawPaths();

    // Draw active path
    ctx.strokeStyle = '#ef4444';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    ctx.beginPath();
    ctx.moveTo(currentActivePath[0].x * pdfScale, currentActivePath[0].y * pdfScale);
    for (let i = 1; i < currentActivePath.length; i++) {
      ctx.lineTo(currentActivePath[i].x * pdfScale, currentActivePath[i].y * pdfScale);
    }
    ctx.lineTo(rawX, rawY);
    ctx.stroke();

    // Calculate live distance
    let currentPixels = 0;
    for (let i = 1; i < currentActivePath.length; i++) {
      const dx = currentActivePath[i].x - currentActivePath[i - 1].x;
      const dy = currentActivePath[i].y - currentActivePath[i - 1].y;
      currentPixels += Math.sqrt(dx * dx + dy * dy);
    }

    const lastPt = currentActivePath[currentActivePath.length - 1];
    const unzoomedX = rawX / pdfScale;
    const unzoomedY = rawY / pdfScale;
    const segDx = unzoomedX - lastPt.x;
    const segDy = unzoomedY - lastPt.y;
    currentPixels += Math.sqrt(segDx * segDx + segDy * segDy);

    const liveDistance = pixelsPerFoot ? currentPixels / pixelsPerFoot : 0;

    if (tooltipRef.current) {
      tooltipRef.current.textContent = `${liveDistance.toFixed(1)} ft`;
      tooltipRef.current.style.left = `${e.clientX + 16}px`;
      tooltipRef.current.style.top = `${e.clientY + 16}px`;
      tooltipRef.current.style.display = 'block';
    }
  };

  const handlePointerLeave = () => {
    if (tooltipRef.current) {
      tooltipRef.current.style.display = 'none';
    }
  };

  return (
    <>
      <canvas
        ref={canvasRef}
        width={viewportWidth}
        height={viewportHeight}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 2,
          display: viewportWidth > 0 ? 'block' : 'none',
          pointerEvents: appMode === 'SELECT' ? 'none' : 'auto',
          cursor: appMode === 'CALIBRATE' ? 'crosshair' : appMode === 'DRAW_WIRE' ? 'crosshair' : 'default',
          touchAction: 'none',
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerLeave={handlePointerLeave}
      />

      {/* Live Tooltip */}
      <div
        ref={tooltipRef}
        style={{
          position: 'fixed',
          background: 'rgba(15, 23, 42, 0.9)',
          color: 'white',
          padding: '4px 8px',
          borderRadius: '6px',
          fontSize: '0.85rem',
          fontWeight: 600,
          pointerEvents: 'none',
          zIndex: 1000,
          display: 'none',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
        }}
      />
    </>
  );
};
